import {
  AppBar,
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React from "react";
import { IoMdCloseCircleOutline } from "react-icons/io";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import camelcaseKeys from "camelcase-keys";
import { apiDelete, apiPut, apiRequest, apiRequestNoLoadder } from "../../utils/api_service";
import { IsSectionVisible } from "../../utils/helper";
import { API_URL } from "../../constants/api";
import { SECTIONS } from "../../constants/appConstants";
import { ShowToast, toastType } from "../../utils/toast-service";
import ConfirmDialog from "../common/Dailogbox/confirmDailog";
import UploadBuildingPhoto from "./UploadBuildingPhoto";
import EditBuildingPhoto from "./EditBuildingPhoto";
import GridTable from "../common/Table/Table";
import moment from "moment";

class BuildingPhotos extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      photos: [],
      configuration: [],
      maxImageCount: null,
      reloadTable: false,
      showUpload: false,
      showEdit: false,
      showDeleteConfirm: false,
      showSetPrimaryConfirm: false,
      pendingPrimaryPhoto: null,
      selectedPhoto: null,
    };
  }

  componentDidMount() {
    this.loadPhotos();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.buildingId !== this.props.buildingId) {
      this.loadPhotos();
    }
  }

  loadPhotos = () => {
    const { buildingId, businessLineId } = this.props;
    if (!buildingId) return;
    apiRequest({
      url: API_URL.Building.getBuildingImages,
      params: { buildingId, businessLineId: businessLineId || 1 },
    }).then(({ data, configuration }) => {
      this.setState({
        photos: camelcaseKeys(data || []),
        configuration: configuration || [],
        reloadTable: !this.state.reloadTable,
      });
    });

    apiRequestNoLoadder({
      url: API_URL.Building.getAppConfig,
      params: { configKey: "BuildingImageMaxCount", businessLineId: businessLineId || 1 },
    }).then(({ data }) => {
      if (data && data.ConfigValue) {
        this.setState({ maxImageCount: parseInt(data.ConfigValue, 10) });
      }
    });
  };

  handleUploadSuccess = () => {
    this.setState({ showUpload: false });
    this.loadPhotos();
    ShowToast("Photo uploaded successfully.", toastType.SUCCESS);
  };

  handleEditSuccess = () => {
    this.setState({ showEdit: false, selectedPhoto: null });
    this.loadPhotos();
    ShowToast("Photo updated successfully.", toastType.SUCCESS);
  };

  handleSetPrimary = (photo) => {
    apiPut(
      API_URL.Building.setBuildingImagePrimary,
      { buildingImageId: photo.buildingImageId },
      {}
    ).then(({ success }) => {
      if (success) {
        this.loadPhotos();
        ShowToast("Primary photo updated.", toastType.SUCCESS);
      } else {
        ShowToast("Failed to set primary photo.", toastType.ERROR);
      }
    });
  };

  handleDeleteConfirm = () => {
    const { selectedPhoto } = this.state;
    apiDelete({
      url: API_URL.Building.deleteBuildingImage,
      params: { buildingImageId: selectedPhoto.buildingImageId },
    }).then(() => {
      this.setState({ showDeleteConfirm: false, selectedPhoto: null });
      this.loadPhotos();
      ShowToast("Photo deleted.", toastType.SUCCESS);
    });
  };

  // Custom renderers that the DB config cannot express
  static CUSTOM_RENDERERS = {
    IsPrimary: "renderPrimaryColumn",
    ThumbnailUrl: "renderThumbnailColumn",
  };

  buildColumnsFromConfig = (configuration) => {
    const dataColumns = [...configuration]
      .filter((c) => c.IsVisible === 1)
      .sort((a, b) => a.AttributeSortOrder - b.AttributeSortOrder)
      .map((c) => ({
        key: c.DBColumn.charAt(0).toLowerCase() + c.DBColumn.slice(1),
        name: c.DisplayName,
        isVisible: true,
        dataFormat: c.DataFormat,
        ...(BuildingPhotos.CUSTOM_RENDERERS[c.DBColumn]
          ? { renderComponent: BuildingPhotos.CUSTOM_RENDERERS[c.DBColumn] }
          : {}),
      }));

    return [
      { key: "thumbnailUrl", name: "Image", isVisible: true, renderComponent: "renderThumbnailColumn" },
      ...dataColumns,
      { key: "action", name: "Action", isVisible: true, renderComponent: "renderActionColumn" },
    ];
  };

  renderThumbnailColumn = (row) => {
    return row.thumbnailUrl ? (
      <img
        src={row.thumbnailUrl}
        alt={row.caption || row.fileName || "photo"}
        style={{ width: "100px", height: "150px", objectFit: "contain", borderRadius: "2px", backgroundColor: "#f5f5f5" }}
      />
    ) : (
      <div
        style={{
          width: "100px",
          height: "150px",
          backgroundColor: "#f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "2px",
          border: "1px solid #ddd",
        }}
      >
        <Typography variant="caption" color="textSecondary" style={{ fontSize: "10px" }}>
          No preview
        </Typography>
      </div>
    );
  };

  renderPrimaryColumn = (row) => {
    const canEdit = IsSectionVisible(
      SECTIONS.Building_Photos,
      SECTIONS.Building_Photos_Listing,
      SECTIONS.ADD_EDIT
    );
    const isDisabled = !!row.isPrimary || !canEdit;
    return (
      <Tooltip title={row.isPrimary ? "Primary photo" : canEdit ? "Set as primary" : "No permission to change primary"}>
        <Checkbox
          checked={!!row.isPrimary}
          color="primary"
          size="small"
          onChange={() => {
            if (!row.isPrimary && canEdit)
              this.setState({ showSetPrimaryConfirm: true, pendingPrimaryPhoto: row });
          }}
          disabled={isDisabled}
          style={{ padding: "0" }}
        />
      </Tooltip>
    );
  };

  renderActionColumn = (row) => {
    const canView = IsSectionVisible(
      SECTIONS.Building_Photos,
      SECTIONS.Building_Photos_Listing
    );
    const canDelete = IsSectionVisible(
      SECTIONS.Building_Photos,
      SECTIONS.Building_Photo_Delete
    );
    return (
      <>
        {canView && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => this.setState({ showEdit: true, selectedPhoto: row })}
              style={{ color: "#064473" }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => this.setState({ showDeleteConfirm: true, selectedPhoto: row })}
              style={{ color: "#064473" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  render() {
    const { photos, configuration, maxImageCount, reloadTable, showUpload, showEdit, showDeleteConfirm, showSetPrimaryConfirm, pendingPrimaryPhoto, selectedPhoto } = this.state;
    const { buildingId, businessLineId } = this.props;

    const canAddEdit = IsSectionVisible(
      SECTIONS.Building_Photos,
      SECTIONS.Building_Photos_Listing,
      SECTIONS.ADD_EDIT
    );

    const columns = this.buildColumnsFromConfig(configuration);

    return (
      <div className="row" style={{ marginTop: "8px" }}>
        {/* Upload Dialog */}
        {showUpload && (
          <Dialog open={showUpload} maxWidth="sm" fullWidth>
            <AppBar style={{ position: "relative", backgroundColor: "#064473", height: "38px" }}>
              <div className="row">
                <div className="col-md-6 col-lg-6" style={{ marginTop: "13px" }}>
                  <span>Upload Building Photo</span>
                </div>
                <div className="col-md-6 col-lg-6">
                  <IconButton
                    style={{ float: "right", marginTop: "-6px", marginRight: "-8px" }}
                    edge="start"
                    color="inherit"
                    onClick={() => this.setState({ showUpload: false })}
                    aria-label="close"
                  >
                    <IoMdCloseCircleOutline />
                  </IconButton>
                </div>
              </div>
            </AppBar>
            <div style={{ padding: "16px" }}>
              <UploadBuildingPhoto
                buildingId={buildingId}
                businessLineId={businessLineId || 1}
                hasExistingPrimary={photos.some((p) => p.isPrimary)}
                onSuccess={this.handleUploadSuccess}
                onCancel={() => this.setState({ showUpload: false })}
              />
            </div>
          </Dialog>
        )}

        {/* Edit Dialog */}
        {showEdit && selectedPhoto && (
          <Dialog open={showEdit} maxWidth="lg" fullWidth>
            <AppBar style={{ position: "relative", backgroundColor: "#064473", height: "38px" }}>
              <div className="row">
                <div className="col-md-6 col-lg-6" style={{ marginTop: "13px" }}>
                  <span>Edit Photo Details</span>
                </div>
                <div className="col-md-6 col-lg-6">
                  <IconButton
                    style={{ float: "right", marginTop: "-6px", marginRight: "-8px" }}
                    edge="start"
                    color="inherit"
                    onClick={() => this.setState({ showEdit: false, selectedPhoto: null })}
                    aria-label="close"
                  >
                    <IoMdCloseCircleOutline />
                  </IconButton>
                </div>
              </div>
            </AppBar>
            <div style={{ padding: "16px" }}>
              <EditBuildingPhoto
                photo={selectedPhoto}
                onSuccess={this.handleEditSuccess}
                onCancel={() => this.setState({ showEdit: false, selectedPhoto: null })}
              />
            </div>
          </Dialog>
        )}

        {/* Set Primary Confirm */}
        {showSetPrimaryConfirm && (
          <ConfirmDialog
            IsOpen={showSetPrimaryConfirm}
            Message="This will set this image as the primary image for the property. Do you want to proceed?"
            OnHandelClick={(confirmed) => {
              if (confirmed) {
                this.handleSetPrimary(pendingPrimaryPhoto);
              }
              this.setState({ showSetPrimaryConfirm: false, pendingPrimaryPhoto: null });
            }}
            AgreeText="Yes, Set as Primary"
            DisAgreeText="Cancel"
          />
        )}

        {/* Delete Confirm */}
        {showDeleteConfirm && (
          <ConfirmDialog
            IsOpen={showDeleteConfirm}
            Message="Are you sure you want to delete this photo? This action cannot be undone."
            OnHandelClick={(confirmed) => {
              if (confirmed) {
                this.handleDeleteConfirm();
              } else {
                this.setState({ showDeleteConfirm: false, selectedPhoto: null });
              }
            }}
            AgreeText="Delete"
            DisAgreeText="Cancel"
          />
        )}

        {/* Toolbar */}
        <div className="col-sm-12 col-lg-12" style={{ marginBottom: "8px", display: "flex", justifyContent: "flex-end" }}>
          {canAddEdit && (
            <Button
              className="action-button no-print"
              size="small"
              onClick={() => {
                if (maxImageCount !== null && photos.length >= maxImageCount) {
                  ShowToast(
                    `Upload limit reached. A maximum of ${maxImageCount} images are allowed per building.`,
                    toastType.ERROR
                  );
                  return;
                }
                this.setState({ showUpload: true });
              }}
            >
              <span className="tool-icon-text">Upload</span>
            </Button>
          )}
        </div>

        {/* Photo Grid */}
        <div className="col-sm-12 col-lg-12">
          <GridTable
            validationMessage={[]}
            rowCount={20}
            config={{}}
            columns={columns}
            data={photos}
            reload={reloadTable}
            renderThumbnailColumn={this.renderThumbnailColumn}
            renderPrimaryColumn={this.renderPrimaryColumn}
            renderActionColumn={this.renderActionColumn}
          />
        </div>
      </div>
    );
  }
}

export default BuildingPhotos;

